import { cid, url as isIpfsUrl } from 'is-ipfs'

/**
 * Validates if a string is a valid CID using is-ipfs library
 */
export const isValidCID = (cidString: string): boolean => {
  if (!cidString || typeof cidString !== 'string') {
    return false
  }
  return cid(cidString)
}

/**
 * Validates if a string is a valid IPFS URL using is-ipfs library
 */
export const isValidIPFSUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') {
    return false
  }
  return isIpfsUrl(url)
}

/**
 * Extracts CID from IPFS URL
 */
export const extractCIDFromUrl = (url: string): string | null => {
  if (!isValidIPFSUrl(url)) {
    return null
  }

  try {
    const urlObj = new URL(url)
    const pathParts = urlObj.pathname.split('/')
    const ipfsIndex = pathParts.findIndex(part => part === 'ipfs')
    if (ipfsIndex === -1 || ipfsIndex + 1 >= pathParts.length) {
      return null
    }
    const cidValue = pathParts[ipfsIndex + 1]
    return isValidCID(cidValue) ? cidValue : null
  } catch (error) {
    console.warn('Error extracting CID from URL:', error)
    return null
  }
}

/**
 * Checks if there are providers for a CID in the IPFS network
 * This function makes HTTP requests to IPFS gateways to check availability
 */
export const checkCIDProviders = async (
  cidString: string,
  options: {
    timeout?: number
    gateways?: string[]
    retries?: number
  } = {}
): Promise<{
  hasProviders: boolean
  availableGateways: string[]
  errors: string[]
}> => {
  const { timeout = 10000, gateways = [], retries = 2 } = options

  // Default IPFS gateways if none provided
  const defaultGateways = [
    'https://ipfs.io',
    'https://gateway.pinata.cloud',
    'https://cloudflare-ipfs.com',
    'https://dweb.link'
  ]

  const gatewaysToCheck = gateways.length > 0 ? gateways : defaultGateways
  const availableGateways: string[] = []
  const errors: string[] = []

  // Validate CID first
  if (!isValidCID(cidString)) {
    return {
      hasProviders: false,
      availableGateways: [],
      errors: ['Invalid CID format']
    }
  }

  // Check each gateway
  const checkPromises = gatewaysToCheck.map(async (gateway) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)

        const response = await fetch(`${gateway}/ipfs/${cidString}`, {
          method: 'HEAD', // Use HEAD to check availability without downloading content
          signal: controller.signal,
          headers: {
            'Accept': '*/*',
            'User-Agent': 'Foster-Marketplace/1.0'
          }
        })

        clearTimeout(timeoutId)

        if (response.ok) {
          availableGateways.push(gateway)
          return { success: true, gateway }
        } else if (response.status === 404) {
          // Content not found on this gateway
          return { success: false, gateway, error: 'Not found' }
        } else {
          // Other error, might be temporary
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      } catch (error: any) {
        const errorMsg = `Gateway ${gateway} attempt ${attempt + 1}: ${error.message}`
        errors.push(errorMsg)
        
        if (attempt === retries) {
          return { success: false, gateway, error: errorMsg }
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
      }
    }
    return { success: false, gateway, error: 'Max retries exceeded' }
  })

  const results = await Promise.all(checkPromises)
  
  return {
    hasProviders: availableGateways.length > 0,
    availableGateways,
    errors: errors.filter((error, index) => errors.indexOf(error) === index) // Remove duplicates
  }
}

/**
 * Checks if IPFS content is available (has providers) with a simple boolean result
 */
export const isValidUrl = async (url: string): Promise<boolean> => {
  if (!url || typeof url !== 'string') {
    return true
  }

  const cidString = extractCIDFromUrl(url)
  if (!cidString) {
    return true
  }

  try {
    const result = await checkCIDProviders(cidString, {
      timeout: 500,
      retries: 1
    })
    return result.hasProviders
  } catch (error) {
    console.warn('Error checking IPFS content availability:', error)
    return true // Assume unavailable on error
  }
}